// src/pages/ProductManagement/ProductManagement.jsx
import React, { useState, useEffect, useMemo } from 'react';
import usePersistentLanguage from '../hooks/usePersistentLanguage';
import { useLocation } from 'react-router-dom';
import styles from './ProductManagement.module.css';
import ProductCard from '../ProductManagement/ProductCard';
import { motion, AnimatePresence } from 'framer-motion';
import { getProducts, addProductQuantity, removeProductQuantity } from '../api/productApi';
import { createRequest } from '../api/requestApi';
import AppLayout from '../components/AppLayout';

// 1) Translation strings for English and Arabic
const translations = {
  en: {
    productManagementTitle: 'Product Management',
    confirm: 'Confirm',
    receipt: 'Receipt',
    areYouSure: 'Are you sure you want to proceed?',
    yesConfirm: 'Yes, Confirm',
    cancel: 'Cancel',
    success: 'Success',
    error: 'Error',
    failedFetchProducts: 'Failed to fetch products. Please try again.',
    pleaseSelectAtLeastOne: 'Please select at least one product with sort, color, and quantity.',
    productsSuccessAdd: 'Products successfully added!',
    productsSuccessRemove: 'Products successfully removed!',
    requestSubmitted: 'Your request has been submitted for manager approval.',
    failedAddProducts: 'Failed to add products. Please try again.',
    failedRemoveProducts: 'Failed to remove products. Please try again.',
    failedSubmitRequest: 'Failed to submit your request. Please try again.',
    close: 'Close',
    homepage: 'Home page',
  },
  ar: {
    productManagementTitle: 'إدارة المنتجات',
    confirm: 'تأكيد',
    receipt: 'الإيصال',
    areYouSure: 'هل أنت متأكد أنك تريد المتابعة؟',
    yesConfirm: 'نعم، تأكيد',
    cancel: 'إلغاء',
    success: 'نجاح',
    error: 'خطأ',
    failedFetchProducts: 'فشل في جلب المنتجات. حاول مرة أخرى.',
    pleaseSelectAtLeastOne: 'يرجى اختيار منتج واحد على الأقل مع الصنف واللون والكمية.',
    productsSuccessAdd: 'تمت إضافة المنتجات بنجاح!',
    productsSuccessRemove: 'تمت إزالة المنتجات بنجاح!',
    requestSubmitted: 'تم إرسال طلبك للموافقة من المدير.',
    failedAddProducts: 'فشل في إضافة المنتجات. حاول مرة أخرى.',
    failedRemoveProducts: 'فشل في إزالة المنتجات. حاول مرة أخرى.',
    failedSubmitRequest: 'فشل في إرسال طلبك. حاول مرة أخرى.',
    close: 'إغلاق',
    homepage: 'الصفحة الرئيسية',
  },
};

const ProductManagement = () => {
  // 2) Add language state and translation helper
  const [language, setLanguage] = usePersistentLanguage();
  const t = (key) => translations[language][key];

  // 3) Toggle language function
  const toggleLanguage = () => {
    setLanguage((prevLang) => (prevLang === 'en' ? 'ar' : 'en'));
  };

  const location = useLocation();
  const action = location.state?.action || 'add';

  const [showModal, setShowModal] = useState(false);
  const [productSelections, setProductSelections] = useState({});
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [products, setProducts] = useState([]);
  const [successMessage, setSuccessMessage] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);

  const token = useMemo(() => localStorage.getItem('authToken'), []);
  const userInfo = useMemo(() => {
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return {
          userType: payload.userType,
          requesterUsername: payload.username,
        };
      } catch (error) {
        console.error('Error parsing token payload:', error);
        return { userType: null, requesterUsername: null };
      }
    }
    return { userType: null, requesterUsername: null };
  }, [token]);

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getProducts()
      .then((response) => {
        setProducts(response.data);
        setLoading(false);
      })
      .catch((error) => {
        console.error('Error fetching products:', error);
        setErrorMessage(t('failedFetchProducts'));
        setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleConfirmClick = () => {
    const selected = Object.values(productSelections).filter(
      (selection) => selection.sortName && selection.color && selection.quantity > 0
    );
    if (selected.length === 0) {
      setErrorMessage(t('pleaseSelectAtLeastOne'));
      return;
    }
    setSelectedProducts(selected);
    setShowModal(true);
  };

  const handleModalConfirm = async () => {
    try {
      for (const product of selectedProducts) {
        const { productId, sortName, color, quantity } = product;
        const prodDetails = products.find((p) => p._id === productId);
        if (!prodDetails) {
          throw new Error('Product not found for the selected item.');
        }

        if (userInfo.userType === 'manager') {
          const payload = { sortName, color, quantity };
          if (action === 'add') {
            await addProductQuantity(prodDetails.SKU, payload);
          } else if (action === 'remove') {
            await removeProductQuantity(prodDetails.SKU, payload);
          }
        } else if (userInfo.userType === 'worker') {
          await createRequest({
            requestId: `REQ-${Date.now()}`,
            productName: prodDetails.name,
            productSKU: prodDetails.SKU,
            requesterUsername: userInfo.requesterUsername || 'unknown',
            state: 'Null',
            sortName,
            color,
            quantity,
            action,
          });
        }
      }

      if (userInfo.userType === 'manager') {
        setSuccessMessage(action === 'add' ? t('productsSuccessAdd') : t('productsSuccessRemove'));
      } else {
        setSuccessMessage(t('requestSubmitted'));
      }
    } catch (error) {
      console.error(`Error processing products for ${action}:`, error);
      if (userInfo.userType === 'manager') {
        setErrorMessage(action === 'add' ? t('failedAddProducts') : t('failedRemoveProducts'));
      } else {
        setErrorMessage(t('failedSubmitRequest'));
      }
    }

    setShowModal(false);
    setProductSelections({});
    setSelectedProducts([]);
  };

  const handleModalCancel = () => {
    setShowModal(false);
  };

  const handleProductSelectionChange = (productId, selection) => {
    setProductSelections((prevSelections) => {
      const currentSelection = prevSelections[productId];
      if (
        currentSelection &&
        currentSelection.sortName === selection.sortName &&
        currentSelection.color === selection.color &&
        currentSelection.quantity === selection.quantity
      ) {
        return prevSelections;
      }
      return { ...prevSelections, [productId]: selection };
    });
  };

  const hasValidSelection = useMemo(
    () =>
      Object.values(productSelections).some(
        (selection) =>
          selection &&
          selection.sortName &&
          selection.color &&
          selection.quantity > 0
      ),
    [productSelections]
  );

  return (
    <AppLayout
      title={t('productManagementTitle')}
      language={language}
      onToggleLanguage={toggleLanguage}
      userType={userInfo.userType}
      showHome
      homeLabel={t('homepage')}
      contentClassName={styles.productManagementPage}
      contentProps={{ 'aria-busy': loading }}
    >
      {loading && (
        <div className={styles.loadingOverlay}>
          <div className={styles.spinner}></div>
        </div>
      )}

      {!loading && (
        <>
          {errorMessage && (
            <div className={styles.errorMessage} role="alert">
              <div className={styles.messageRow}>
                <span className={styles.messageText}>{errorMessage}</span>
                <button
                  type="button"
                  className={styles.messageCloseButton}
                  onClick={() => setErrorMessage(null)}
                >
                  {t('close')}
                </button>
              </div>
            </div>
          )}

          {successMessage && (
            <div className={styles.successMessage}>
              <div className={styles.messageRow}>
                <span className={styles.messageText}>{successMessage}</span>
                <button
                  type="button"
                  className={styles.messageCloseButton}
                  onClick={() => setSuccessMessage(null)}
                >
                  {t('close')}
                </button>
              </div>
            </div>
          )}

          <section className={styles.productList}>
            <AnimatePresence>
              {products.map((product) => (
                <ProductCard
                  key={product._id}
                  productId={product._id}
                  hasImage={product.hasImage}
                  name={product.name}
                  size={product.size}
                  sku={product.SKU}
                  sorts={product.sorts || []}
                  language={language}
                  onSelectionChange={(selection) =>
                    handleProductSelectionChange(product._id, selection)
                  }
                  action={action}
                />
              ))}
            </AnimatePresence>
          </section>

          <div className={styles.actionBar}>
            <button
              className={styles.confirmButton}
              onClick={handleConfirmClick}
              disabled={!hasValidSelection}
            >
              {t('confirm')}
            </button>
          </div>
        </>
      )}

      <AnimatePresence>
        {showModal && (
          <motion.div
            className={styles.modalBackdrop}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className={styles.modalContent}
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
            >
              <h2 className={styles.modalTitle}>{t('receipt')}</h2>
              <ul className={styles.receiptList}>
                {selectedProducts.map((product, index) => (
                  <li key={index} className={styles.receiptItem}>
                    <strong>{product.name || ''}</strong> · {product.sortName} · {product.color} ·{' '}
                    {product.quantity}
                  </li>
                ))}
              </ul>
              <p className={styles.modalSubtitle}>{t('areYouSure')}</p>
              <div className={styles.modalActions}>
                <button className={styles.modalConfirmButton} onClick={handleModalConfirm}>
                  {t('yesConfirm')}
                </button>
                <button className={styles.modalCancelButton} onClick={handleModalCancel}>
                  {t('cancel')}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </AppLayout>
  );
};

export default ProductManagement;
